import React, { useEffect, useRef, useState } from 'react'
import { Button, Dropdown, Form } from 'semantic-ui-react'
import useAxios from 'hooks/useAxios'
import toast from 'libs/toast'
import uuid from 'uuid/v4'
import useSaveUserActivityLog, { ANSWER_TOPIC_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import * as R from 'ramda'
import useUser from 'hooks/useUser'
import { validateRequiredText } from 'libs/form-validation'
import ErrorText from 'shared/components/forms/ErrorText'

const CreateUpdateTopicModal = ({
  modal: Modal,
  isOpenTopicModal,
  close,
  topicGroups,
  editingTopic,
  setEditingTopic,
  setTopicState,
  topicData,
}) => {
  const hiddenRefButton = useRef(null)
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)
  const [nameError, setNameError] = useState('')

  useEffect(() => {
    if (!isOpenTopicModal) {
      setNameError('')
    }
  }, [isOpenTopicModal])

  const saveUserActivityLog = useSaveUserActivityLog()
  const topicGroupsOptions = topicGroups.map((group) => ({
    key: group.key,
    id: group.id,
    text: group.name,
    value: group.name,
    isPredefined: group.is_predefined,
  }))

  const { fetch: createTopic, loading: isCreatingTopic } = useAxios({
    method: 'post',
    url: 'responses/topics/',
    skip: true,
  })

  const { fetch: updateTopic, loading: isUpdatingTopic } = useAxios({
    method: 'patch',
    url: `responses/topics/${editingTopic.id}/`,
    skip: true,
  })

  function handleSubmit() {
    const error = validateRequiredText('Topic name', editingTopic.name || '')
    if (error) {
      setNameError(error)
      return
    }

    if (editingTopic.id) {
      handleUpdateTopic()
    } else {
      handleCreateTopic()
    }
  }

  function handleCreateTopic() {
    const existedTopics = [...topicData]
    const isDuplicate = existedTopics.some(
      (topic) => topic.name.toLowerCase() === editingTopic.name.toLowerCase()
    )

    if (isDuplicate) {
      toast.error('The topic you are trying to create already exists.')
      return
    }

    const data = {
      name: editingTopic.name,
      description: editingTopic.description,
      is_predefined: false,
      groups: editingTopic.groups,
    }

    createTopic({ data })
      .then((res) => {
        if (res.data) {
          setTopicState((prev) => [...prev, { ...res.data, key: uuid() }])

          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.create_new_topic,
            statusCode: res.status,
            requestBody: data,
            target: `com_${companyId}`,
          })

          toast.success('New topic created!')
          close()
        }
      })
      .catch((err) => {
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.create_new_topic,
          statusCode: err.response.status,
          errorResponseBody: err.response.data,
          requestBody: data,
          target: `com_${companyId}`,
        })
        toast.error(
          'Uh oh. There was a problem creating the new topic. Please try again or contact Simplesat support.'
        )
        console.error(err)
      })
  }

  function handleUpdateTopic() {
    const existedTopics = topicData
    const isNameChanged = editingTopic.name === editingTopic.original_name
    const isDuplicate = existedTopics.some(
      (topic) => topic.name.toLowerCase() === editingTopic.name.toLowerCase()
    )

    if (!isNameChanged && isDuplicate) {
      toast.error('The topic you are trying to update already exists.')

      return
    }

    const data = {
      name: editingTopic.name,
      description: editingTopic.description,
      groups: editingTopic.groups,
    }

    updateTopic({ data })
      .then((res) => {
        if (res.data) {
          setTopicState((prev) => {
            const tempResults = prev.map((topic) => {
              return topic.key === editingTopic.key ? { ...res.data, key: uuid() } : topic
            })
            return tempResults
          })

          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.edit_topic,
            statusCode: res.status,
            requestBody: data,
            target: `topic_${editingTopic.id}`,
          })

          toast.success('Topic updated!')
          close()
        }
      })
      .catch((err) => {
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.edit_topic,
          statusCode: err.response.status,
          errorResponseBody: err.response.data,
          requestBody: data,
          target: `topic_${editingTopic.id}`,
        })

        toast.error(
          'Uh oh. There was a problem updating the topic. Please try again or contact Simplesat support.'
        )
        console.error(err)
      })
  }

  function handleChange(e, name, data) {
    if (name === 'name' || name === 'description') {
      if (name === 'name' && nameError) {
        setNameError('')
      }
      setEditingTopic((prev) => ({ ...prev, [name]: e.target.value }))
    } else {
      // Handle changes for the topicGroup dropdown
      const selectedValues = data.value

      // Separate predefinedGroups and topicGroups based on `isPredefined` property
      const topicGroups = []

      selectedValues.forEach((value) => {
        const option = topicGroupsOptions.find((opt) => opt.value === value)
        topicGroups.push({ id: option.id, name: option.text, is_predefined: option.isPredefined })
      })

      setEditingTopic((prev) => ({
        ...prev,
        groupValues: selectedValues,
        groups: topicGroups,
      }))
    }
  }

  return (
    <Modal open={isOpenTopicModal} onClose={close} size="small">
      <Modal.Header>{editingTopic.id ? 'Edit topic' : 'Create new topic'}</Modal.Header>
      <Modal.Content>
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Field error={Boolean(nameError)}>
            <label>Topic name</label>
            <input
              value={editingTopic.name}
              onChange={(e) => handleChange(e, 'name')}
              maxLength={200}
            />
            {nameError && <ErrorText>{nameError}</ErrorText>}
          </Form.Field>

          <Form.Field>
            <label>Description</label>
            <input
              name="description"
              value={editingTopic.description}
              onChange={(e) => handleChange(e, 'description')}
              maxLength={300}
            />
            <div className="ui small info message">
              Provide a brief description to clarify what this topic covers. This will help users
              understand its purpose and improve the accuracy of our topic detection. Example:
              'Timeliness: Feedback on the promptness of service or response.'
            </div>
          </Form.Field>

          <Form.Field>
            <label>Group</label>
            <Dropdown
              onChange={(e, data) => handleChange(e, 'topicGroup', data)}
              multiple
              selection
              search
              options={topicGroupsOptions}
              value={editingTopic.groupValues}
            />
          </Form.Field>

          <input type="submit" hidden ref={hiddenRefButton} />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={close}>Cancel</Button>
        <Button
          onClick={() => hiddenRefButton.current.click()}
          disabled={isCreatingTopic || isUpdatingTopic}
          loading={isCreatingTopic || isUpdatingTopic}
          primary
        >
          {editingTopic.id ? 'Save' : 'Create topic'}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default CreateUpdateTopicModal



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Topics/TopicModal.js